// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { GripVertical, Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useStoreDetailQuery } from "@/api/hooks/use-stores";
import { InlineSpinner } from "@/components/shared/inline-spinner";
import { LinearIcon } from "@/components/shared/linear-icon";
import { SaveDiscardPane } from "@/components/shared/save-discard-pane";
import { ScheduleLabelLegend } from "@/components/shared/schedule-label-chip";
import { ScheduleTimeline } from "@/components/shared/schedule-timeline";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { linearIssueUrl, useLinearLinkMode } from "@/lib/linear";
import { cn } from "@/lib/utils";
import { ScheduleEditor } from "./schedule/schedule-editor";
import { ScheduleExceptionDialog } from "./schedule/schedule-exception-dialog";
import { type CaptureWindow, toSegments } from "./schedule/schedule-utils";
import type { ScheduleException, ScheduleSettings, StoreSchedule } from "./schedule/types";
import { useStoreScheduleQuery, useUpdateStoreScheduleMutation } from "./schedule/use-store-schedule";

/** Parse a `HH:MM[:SS]` time string into minutes since midnight. */
function timeToMinutes(value?: string): number | null {
  if (!value) return null;
  const [h, m] = value.split(":");
  const hh = Number(h);
  const mm = Number(m);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  return hh * 60 + mm;
}

export interface StoreScheduleTabHandle {
  reset: () => void;
}

interface StoreScheduleTabProps {
  storeName: string;
  onDirtyChange?: (dirty: boolean) => void;
  ref?: React.Ref<StoreScheduleTabHandle>;
}

export function StoreScheduleTab({ storeName, onDirtyChange, ref }: StoreScheduleTabProps) {
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.storesScheduleEdit);
  const { data: loaded } = useStoreScheduleQuery(storeName);
  const { data: storeRaw } = useStoreDetailQuery(storeName);
  const updateSchedule = useUpdateStoreScheduleMutation(storeName);

  // every_hour / no_of_images windows are laid out within the store's camera on/off hours.
  const cameras = (storeRaw as { cameras?: { onTime?: string; offTime?: string } } | undefined)?.cameras;
  const onMin = timeToMinutes(cameras?.onTime);
  const offMin = timeToMinutes(cameras?.offTime);
  const cameraWindow: CaptureWindow | undefined =
    onMin != null && offMin != null && offMin > onMin ? { start: onMin, end: offMin } : undefined;

  // Store open/close hours — shaded as "closed" on the timeline backdrop.
  const details = (storeRaw as { details?: { openTime?: string; closeTime?: string } } | undefined)?.details;
  const openMin = timeToMinutes(details?.openTime);
  const closeMin = timeToMinutes(details?.closeTime);
  const openWindow: CaptureWindow | undefined =
    openMin != null && closeMin != null && closeMin > openMin ? { start: openMin, end: closeMin } : undefined;

  const [working, setWorking] = useState<StoreSchedule | null>(null);
  const baselineRef = useRef<StoreSchedule | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ScheduleException | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  useEffect(
    function seedWorkingWhenLoaded() {
      if (loaded) {
        setWorking(structuredClone(loaded));
        baselineRef.current = structuredClone(loaded);
      }
    },
    [loaded],
  );

  const isDirty = !!working && !!baselineRef.current && JSON.stringify(working) !== JSON.stringify(baselineRef.current);

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (baselineRef.current) setWorking(structuredClone(baselineRef.current));
    },
  }));

  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(
    function notifyParentOfDirtyState() {
      onDirtyChangeRef.current?.(isDirty);
    },
    [isDirty],
  );

  if (!working) {
    return <InlineSpinner className="py-16" />;
  }

  function setBase(base: ScheduleSettings) {
    setWorking((prev) => (prev ? { ...prev, base } : prev));
  }

  function upsertException(exception: ScheduleException) {
    setWorking((prev) => {
      if (!prev) return prev;
      const exists = prev.exceptions.some((e) => e.id === exception.id);
      return {
        ...prev,
        exceptions: exists
          ? prev.exceptions.map((e) => (e.id === exception.id ? exception : e))
          : [...prev.exceptions, exception],
      };
    });
  }

  function removeException(id: string) {
    setWorking((prev) => (prev ? { ...prev, exceptions: prev.exceptions.filter((e) => e.id !== id) } : prev));
  }

  function reorderExceptions(from: number, to: number) {
    setWorking((prev) => {
      if (!prev) return prev;
      const n = prev.exceptions.length;
      if (from === to || from < 0 || to < 0 || from >= n || to >= n) return prev;
      const next = [...prev.exceptions];
      const [moved] = next.splice(from, 1);
      if (!moved) return prev;
      next.splice(to, 0, moved);
      return { ...prev, exceptions: next };
    });
  }

  async function handleSave() {
    if (!working) return;
    try {
      await updateSchedule.mutateAsync(working);
      baselineRef.current = structuredClone(working);
      setWorking(structuredClone(working));
      toast.success(t("stores.schedule.notifications.updated", { name: storeName }));
    } catch {
      // Error toast handled by API middleware
    }
  }

  function openAdd() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(exception: ScheduleException) {
    setEditing(exception);
    setDialogOpen(true);
  }

  const exceptions = working.exceptions;

  return (
    <form
      className="max-w-3xl space-y-6 p-6"
      onSubmit={(e) => {
        e.preventDefault();
        handleSave();
      }}
    >
      <div>
        <div className="mb-3 flex items-start justify-between gap-4">
          <h3 className="text-sm font-medium leading-none">{t("stores.schedule.everyday")}</h3>
          <ScheduleLabelLegend className="shrink-0 leading-none" />
        </div>
        <ScheduleEditor
          settings={working.base}
          onChange={setBase}
          editable={canEdit}
          window={cameraWindow}
          openWindow={openWindow}
        />
      </div>

      <Separator />

      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-medium leading-none">{t("stores.schedule.exceptions")}</h3>
          {canEdit && (
            <Button type="button" variant="outline" size="sm" className="uppercase" onClick={openAdd}>
              <Plus className="h-4 w-4" />
              {t("stores.schedule.add")}
            </Button>
          )}
        </div>

        {exceptions.length === 0 ? (
          <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            {t("stores.schedule.noExceptions")}
          </p>
        ) : (
          <div className="space-y-2">
            {exceptions.map((exception, i) => (
              <ExceptionCard
                key={exception.id}
                exception={exception}
                index={i}
                canEdit={canEdit}
                dragging={dragIndex === i}
                window={cameraWindow}
                openWindow={openWindow}
                onEdit={() => openEdit(exception)}
                onRemove={() => removeException(exception.id)}
                onDragStart={() => setDragIndex(i)}
                onDragEnterCard={() => {
                  if (dragIndex !== null && dragIndex !== i) {
                    reorderExceptions(dragIndex, i);
                    setDragIndex(i);
                  }
                }}
                onDragEnd={() => setDragIndex(null)}
              />
            ))}
          </div>
        )}
      </div>

      {isDirty && (
        <SaveDiscardPane
          onDiscard={() => baselineRef.current && setWorking(structuredClone(baselineRef.current))}
          saving={updateSchedule.isPending}
          saveDisabled={!canEdit}
        />
      )}

      <ScheduleExceptionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        exception={editing}
        baseSettings={working.base}
        window={cameraWindow}
        openWindow={openWindow}
        onSave={upsertException}
      />
    </form>
  );
}

interface ExceptionCardProps {
  exception: ScheduleException;
  index: number;
  canEdit: boolean;
  dragging: boolean;
  window?: CaptureWindow;
  openWindow?: CaptureWindow;
  onEdit: () => void;
  onRemove: () => void;
  onDragStart: () => void;
  onDragEnterCard: () => void;
  onDragEnd: () => void;
}

function ExceptionCard({
  exception,
  index,
  canEdit,
  dragging,
  window,
  openWindow,
  onEdit,
  onRemove,
  onDragStart,
  onDragEnterCard,
  onDragEnd,
}: ExceptionCardProps) {
  const linkMode = useLinearLinkMode();
  // Native DnD only fires from the handle: arm on handle pointer-down, cancel drags otherwise.
  const dragFromHandle = useRef(false);
  return (
    <div
      draggable={canEdit}
      onDragStart={(e) => {
        if (!dragFromHandle.current) {
          e.preventDefault();
          return;
        }
        onDragStart();
      }}
      onDragEnter={onDragEnterCard}
      onDragOver={(e) => e.preventDefault()}
      onDragEnd={() => {
        dragFromHandle.current = false;
        onDragEnd();
      }}
      className={cn("rounded-lg border p-3", dragging && "opacity-50")}
    >
      <div className="flex items-start gap-2">
        {canEdit && (
          <span
            onPointerDown={() => {
              dragFromHandle.current = true;
            }}
            onPointerUp={() => {
              dragFromHandle.current = false;
            }}
            className="-my-1.5 -ml-1.5 flex h-9 w-8 shrink-0 cursor-grab items-center justify-center text-muted-foreground hover:text-foreground"
          >
            <GripVertical className="h-4 w-4" />
          </span>
        )}
        <span className="rounded-md bg-muted px-2 py-1 text-xs font-medium tabular-nums">#{index + 1}</span>
        <span className={cn("pt-0.5 text-sm", exception.name ? "font-medium" : "font-mono font-normal")}>
          {exception.name || exception.expression}
        </span>
        {exception.tickets?.map((ticket) => (
          <a
            key={ticket}
            href={linearIssueUrl(ticket, linkMode)}
            target={linkMode === "web" ? "_blank" : undefined}
            rel={linkMode === "web" ? "noreferrer" : undefined}
            draggable={false}
            className="-my-1 inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <LinearIcon className="size-3.5" />
            {ticket}
          </a>
        ))}
        {canEdit && (
          <div className="ml-auto flex items-center gap-1">
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={onRemove}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      <div className={cn("mt-2", canEdit && "pl-6")}>
        <ScheduleTimeline
          strategy={exception.definition.type}
          segments={toSegments(exception.definition, window)}
          size="sm"
          showHours
          openWindow={openWindow}
        />
      </div>
    </div>
  );
}
